import React, { useMemo, useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Shield, X } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";

// Import des interfaces principales
import MOOCaisseInterface from "./components/CaisseInterface";
import MOOCuisineInterface from "./components/CuisineInterface";
import MOOBarInterface from "./components/BarInterface";
import GenericKdsInterface from "./components/GenericKdsInterface";
import Navigation from "./components/Navigation";
import ProtectedRoute from "./components/ProtectedRoute";

// Import des composants admin
import AdminLayout from "./components/AdminLayout";
import AdminInterface from "./components/AdminInterface";
import SalesInterface from "./components/SalesInterface";
import ProductManagement from "./components/ProductManagement";
import CategoryManagement from "./components/CategoryManagement";
import MediaManagement from "./components/MediaManagement";
import UserManagement from "./components/UserManagement";
import CustomerManagement from "./components/CustomerManagement";
import TableManagement from "./components/TableManagement";
import SessionManagement from "./components/SessionManagement";
import Reports from "./components/Reports";
import CashReports from "./components/CashReports";
import Settings from "./components/Settings";
import WaiseLegacyPage from "./components/waise-v2/WaiseLegacyPage";
import WaiseV2Interface from "./components/waise-v2/WaiseV2Interface";
import WaiseV2Boundary from "./components/waise-v2/WaiseV2Boundary";
import Permissions from "./components/Permissions";
import CancellationHistory from "./components/CancellationHistory";
import DiscountHistory from "./components/DiscountHistory";
import SplashOverlay from "./components/SplashOverlay";
import FirstRunSetup from "./components/FirstRunSetup";
import DualScreenDisplay from "./components/DualScreenDisplay";
import MenuPage from "./components/MenuPage";
import CloudLanding from "./components/CloudLanding";
import CloudTenantMenu from "./components/CloudTenantMenu";
import CloudLogin from "./components/CloudLogin";
import CloudDashboard from "./components/CloudDashboard";
import { installAuthenticatedFetchInterceptor } from "./utils/apiClient";
import { isCloudSessionValidForHost } from "./utils/cloudBrowserSession";

installAuthenticatedFetchInterceptor();

// Configuration de l'application
const APP_CONFIG = {
  name: "MOO Sync",
  version: "2.0.0",
  author: "Djizef SARL",
  description: "Système POS le plus avancé d'Afrique de l'Ouest",
};

// Helpers pour un accès storage sécurisé (private mode / sandbox iframe)
const safeGetItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeParseJSON = (value, fallback = {}) => {
  try {
    return JSON.parse(value ?? "");
  } catch (e) {
    return fallback;
  }
};

const isCloudAuthenticated = () => {
  try {
    if (typeof window === "undefined") return false;
    const ls = window.localStorage;
    if (!ls) return false;
    const rawUser = ls.getItem("moo_cloud_user");
    const token = ls.getItem("moo_cloud_token");
    const user = safeParseJSON(rawUser, null);
    return isCloudSessionValidForHost({
      hostname: window.location?.hostname || "",
      user,
      token,
    });
  } catch (e) {
    return false;
  }
};

const isCloudHost = () => {
  try {
    if (typeof window === "undefined") return false;
    const host = window.location?.hostname || "";
    // Local pur
    if (
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "0.0.0.0" ||
      host === "::1"
    ) {
      return false;
    }

    // Hostnames LAN (mDNS / réseau interne)
    if (host.endsWith(".local") || host.endsWith(".lan")) {
      return false;
    }

    // IP privées LAN : 10/8, 172.16/12, 192.168/16 (+ link-local 169.254/16)
    const ipMatch = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
    if (ipMatch) {
      const parts = ipMatch.slice(1).map((p) => Number.parseInt(p, 10));
      const [a, b] = parts;
      const valid = parts.every((n) => Number.isFinite(n) && n >= 0 && n <= 255);
      if (valid) {
        const isPrivate =
          a === 10 ||
          (a === 192 && b === 168) ||
          (a === 172 && b >= 16 && b <= 31) ||
          (a === 169 && b === 254);
        if (isPrivate) return false;
      }
    }

    // Domaines moosync publics => cloud
    if (host.includes("moosync")) return true;

    // Par défaut : cloud (domaine/IP public)
    return true;
  } catch (_e) {
    return false;
  }
};

const isCloudTenantHost = () => {
  try {
    if (typeof window === "undefined") return false;
    const host = window.location?.hostname || "";
    if (!host) return false;
    if (!isCloudHost()) return false;
    const parts = host.split(".").filter(Boolean);
    // Ex: tenant.moosync.com => 3+ segments. Le domaine racine (moosync.com) reste le "hub".
    if (parts.length < 3) return false;
    const sub = parts[0].toLowerCase();
    if (!sub || sub === "www") return false;
    return true;
  } catch (_e) {
    return false;
  }
};

const getCloudAuthenticatedRole = () => {
  try {
    const user = safeParseJSON(safeGetItem("moo_cloud_user"), {});
    return String(user?.role || "").toLowerCase();
  } catch (_e) {
    return "";
  }
};

const WaiseStandalonePage = () => {
  return (
    <div className="min-h-screen bg-[#050b18] text-white">
      <WaiseV2Boundary>
        <WaiseV2Interface />
      </WaiseV2Boundary>
    </div>
  );
};

function App() {
  const [isElectron, setIsElectron] = useState(false);
  const [currentPath, setCurrentPath] = useState("/");
  const [cloudAuthVersion, setCloudAuthVersion] = useState(0);
  const cloudAuthKey = useMemo(
    () => `cloud-auth-${cloudAuthVersion}`,
    [cloudAuthVersion],
  );
  const [systemStatus, setSystemStatus] = useState({
    server: "disconnected",
    database: "disconnected",
    printers: "unknown",
  });

  // Détecter si on est dans Electron ou navigateur web
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isElectronEnv = userAgent.indexOf("electron") > -1;
    setIsElectron(isElectronEnv);
    document.documentElement.classList.toggle("electron", isElectronEnv);
    const updateViewportVars = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--moo-app-vh", `${vh}px`);
      document.documentElement.style.setProperty(
        "--moo-app-height",
        `calc(var(--moo-app-vh) * 100)`,
      );
    };
    updateViewportVars();
    window.addEventListener("resize", updateViewportVars);
    window.addEventListener("orientationchange", updateViewportVars);
    document.addEventListener("fullscreenchange", updateViewportVars);
    window.visualViewport?.addEventListener("resize", updateViewportVars);

    // Initialiser la connexion système
    initializeSystem();
    return () => {
      window.removeEventListener("resize", updateViewportVars);
      window.removeEventListener("orientationchange", updateViewportVars);
      document.removeEventListener("fullscreenchange", updateViewportVars);
      window.visualViewport?.removeEventListener("resize", updateViewportVars);
    };
  }, []);

  useEffect(() => {
    const bump = () => setCloudAuthVersion((v) => v + 1);
    const handleStorage = (event) => {
      // Éviter de remonter TOUTE l'app sur n'importe quel localStorage.setItem
      // (ça provoque des "redémarrages" de l'UI caisse/admin quand d'autres onglets écrivent en storage).
      const key = event?.key;
      if (!key) return;
      if (key === "moo_cloud_user" || key === "moo_cloud_token") {
        bump();
      }
    };
    window.addEventListener("cloud-auth-changed", bump);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("cloud-auth-changed", bump);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const initializeSystem = async () => {
    try {
      // Tester la connexion au serveur local
      const response = await fetch("/api/status");
      if (response.ok) {
        setSystemStatus((prev) => ({ ...prev, server: "connected" }));
      }
    } catch (error) {
      console.log("Serveur non disponible - Mode standalone");
      setSystemStatus((prev) => ({ ...prev, server: "standalone" }));
    }
  };

  // Composant de statut système (masqué car intégré dans footer)
  const SystemStatus = () => null;

  const ToastCloseButton = ({ closeToast }) => (
    <button
      type="button"
      onClick={closeToast}
      aria-label="Fermer la notification"
      className="absolute top-3 right-3 text-white/50 hover:text-white transition-colors"
    >
      <X className="w-4 h-4" />
    </button>
  );

  const SandboxBadgeOverlay = () => {
    const location = useLocation();
    const [label, setLabel] = useState("SANDBOX");
    const [isSandbox, setIsSandbox] = useState(true);
    const isSuperadminPath = location.pathname.startsWith("/superadmin");

    useEffect(() => {
      if (!isSuperadminPath) return undefined;
      let cancelled = false;
      (async () => {
        try {
          const res = await fetch("/api/status");
          const data = res.ok ? await res.json() : null;
          if (cancelled || !data) return;
          const enabled = Boolean(data.superadminSandboxEnabled);
          setIsSandbox(enabled);
          setLabel(enabled ? "SANDBOX" : "SUPERADMIN");
        } catch (_e) {
          // fallback : afficher SANDBOX (le plus prudent visuellement)
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [isSuperadminPath]);

    if (!isSuperadminPath) return null;
    return (
      <div className="pointer-events-none fixed top-3 right-3 z-[9999]">
        <div
          className={`pointer-events-auto inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.25em] shadow-lg backdrop-blur ${
            isSandbox
              ? "border-amber-400/40 bg-amber-500/15 text-amber-200"
              : "border-sky-400/40 bg-sky-500/15 text-sky-200"
          }`}
        >
          <Shield size={14} />
          {label}
        </div>
      </div>
    );
  };

  const ManifestRouteSync = () => {
    const location = useLocation();

    useEffect(() => {
      if (typeof document === "undefined" || typeof window === "undefined") return;
      const host = String(window.location?.hostname || "").toLowerCase();
      const isLocalHost =
        host === "localhost" ||
        host === "127.0.0.1" ||
        host === "0.0.0.0" ||
        host.endsWith(".local") ||
        host.endsWith(".lan") ||
        /^10\./.test(host) ||
        /^192\.168\./.test(host) ||
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(host) ||
        /^169\.254\./.test(host);

      const path = String(location.pathname || "/").toLowerCase();
      const entry = path.startsWith("/kds/")
        ? path
        : ["/caisse", "/cuisine", "/bar"].find((candidate) =>
            path.startsWith(candidate),
          );
      const targetHref =
        isLocalHost && entry
          ? `/manifest.json?entry=${encodeURIComponent(entry)}`
          : "/manifest.json";

      const manifestLinks = Array.from(
        document.querySelectorAll('link[rel="manifest"]'),
      );
      manifestLinks.forEach((link) => {
        if (link.getAttribute("href") !== targetHref) {
          link.setAttribute("href", targetHref);
        }
      });
    }, [location.pathname]);

    return null;
  };

  const CloudBlocked = ({ title, description }) => (
    <div className="moo-app-screen bg-gradient-to-br from-[#0a1224] via-[#0b152d] to-[#050b18] text-white flex items-center justify-center px-6">
      <div className="w-full max-w-lg rounded-3xl bg-white/5 border border-white/10 p-6 shadow-2xl">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
            <img
              src="/moo-sync-insigne-logo.png"
              alt="MOO Sync"
              className="h-7 w-7 object-contain"
            />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/60">MOO Sync</p>
            <h1 className="text-xl font-semibold">{title}</h1>
          </div>
        </div>
        <p className="mt-4 text-white/70">{description}</p>
        <p className="mt-6 text-xs text-white/40">
          Cette interface est réservée aux installations locales.
        </p>
      </div>
    </div>
  );

  return (
    <Router>
      <SplashOverlay />
      <FirstRunSetup />
      <div className="App moo-app-screen bg-gray-100">
        <ManifestRouteSync />
        <SandboxBadgeOverlay />
        <ToastContainer
          position="top-right"
          autoClose={3200}
          hideProgressBar
          newestOnTop
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable={false}
          pauseOnHover
          limit={4}
          icon={false}
          toastClassName={() =>
            "relative overflow-hidden bg-transparent shadow-none p-0 pointer-events-auto"
          }
          bodyClassName={() => "p-0 m-0"}
          progressClassName="bg-white/40"
          closeButton={(props) => <ToastCloseButton {...props} />}
        />
        {/* Navigation pour le mode web - admins seulement */}
        {!isElectron &&
          (() => {
            const currentUser = safeParseJSON(safeGetItem("moo_user"), {});
            return currentUser.role === "admin" && <Navigation />;
          })()}

        {/* Routes principales */}
        <Routes>
          {/* Menu public (catalogue) */}
          <Route path="/menu" element={<MenuPage />} />

          {/* Cloud / Djizef */}
          <Route
            path="/cloud"
            element={
              isCloudTenantHost()
                ? <Navigate to="/login" replace />
                : <CloudLanding />
            }
          />
          <Route
            path="/cloud/login"
            // Cette route reste toujours un écran de connexion/redirection.
            // Afficher CloudDashboard ici créait une course après un login
            // tenant : le dashboard siège appelait ses API, recevait 401/403,
            // puis supprimait la session établissement avant navigate().
            element={
              isCloudTenantHost()
                ? <Navigate to="/login" replace />
                : <CloudLogin key={cloudAuthKey} />
            }
          />
          <Route
            path="/cloud/dashboard"
            element={
              isCloudTenantHost()
                ? <Navigate to="/login" replace />
                : isCloudAuthenticated()
                ? <CloudDashboard key={cloudAuthKey} />
                : <CloudLogin key={cloudAuthKey} />
            }
          />
          <Route
            path="/cloud/clients"
            element={
              isCloudTenantHost()
                ? <Navigate to="/login" replace />
                : isCloudAuthenticated()
                ? <CloudDashboard key={cloudAuthKey} />
                : <CloudLogin key={cloudAuthKey} />
            }
          />
          <Route
            path="/cloud/clients/:tenantId"
            element={
              isCloudTenantHost()
                ? <Navigate to="/login" replace />
                : isCloudAuthenticated()
                ? <CloudDashboard key={cloudAuthKey} />
                : <CloudLogin key={cloudAuthKey} />
            }
          />
          <Route
            path="/cloud/activations"
            element={
              isCloudTenantHost()
                ? <Navigate to="/login" replace />
                : isCloudAuthenticated()
                ? <CloudDashboard key={cloudAuthKey} />
                : <CloudLogin key={cloudAuthKey} />
            }
          />
          <Route
            path="/login"
            element={
              // /login sert à 2 cas :
              // - Domaine "hub" (moosync.com) : portail Djizef (CloudDashboard)
              // - Sous-domaine établissement (tenant.moosync.com) : login admin/manager + accès /admin
              isCloudTenantHost()
                ? (
                    isCloudAuthenticated()
                      ? (
                          <Navigate
                            to={getCloudAuthenticatedRole() === "stockeur" ? "/waise" : "/admin"}
                            replace
                          />
                        )
                      : <CloudLogin key={cloudAuthKey} />
                  )
                : (isCloudAuthenticated() ? <CloudDashboard key={cloudAuthKey} /> : <CloudLogin key={cloudAuthKey} />)
            }
          />

          {/* Routes protégées */}
          <Route
            path="/"
            element={
              isCloudHost()
                ? (isCloudTenantHost() ? <CloudTenantMenu /> : <CloudLanding />)
                : (
                    <ProtectedRoute requiredRole="cashier">
                      <MOOCaisseInterface />
                    </ProtectedRoute>
                  )
            }
          />
          <Route
            path="/caisse"
            element={
              isCloudHost()
                ? (
                    <CloudBlocked
                      title="Caisse indisponible"
                      description="La caisse n'est pas accessible depuis le cloud."
                    />
                  )
                : (
                    <ProtectedRoute requiredRole="cashier">
                      <MOOCaisseInterface />
                    </ProtectedRoute>
                  )
            }
          />

          {/* Routes cuisine et bar - accès direct sur le réseau local */}
          <Route
            path="/cuisine"
            element={
              isCloudHost()
                ? (
                    <CloudBlocked
                      title="Cuisine indisponible"
                      description="La cuisine n'est pas accessible depuis le cloud."
                    />
                  )
                : <MOOCuisineInterface />
            }
          />
          <Route
            path="/bar"
            element={
              isCloudHost()
                ? (
                    <CloudBlocked
                      title="Bar indisponible"
                      description="Le bar n'est pas accessible depuis le cloud."
                    />
                  )
                : <MOOBarInterface />
            }
          />
          <Route
            path="/kds/:destination"
            element={
              isCloudHost()
                ? <CloudBlocked title="KDS indisponible" description="Les KDS ne sont accessibles que sur le réseau local." />
                : <GenericKdsInterface />
            }
          />
          <Route path="/display" element={<DualScreenDisplay />} />

          <Route
            path="/waise"
            element={
              <ProtectedRoute requiredRole="stockeur">
                <WaiseStandalonePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/waise-v1"
            element={
              <ProtectedRoute requiredRole="stockeur">
                <WaiseLegacyPage />
              </ProtectedRoute>
            }
          />

          {/* WAISE dispose de son propre espace : ne pas l'imbriquer dans AdminLayout. */}
          <Route
            path="/admin/waise"
            element={
              <ProtectedRoute requiredRole="admin">
                <WaiseStandalonePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/waise"
            element={
              <ProtectedRoute requiredRole="superadmin">
                <WaiseStandalonePage />
              </ProtectedRoute>
            }
          />

          {/* Routes administrateur avec layout protégé */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminInterface />} />
            <Route path="dashboard" element={<AdminInterface />} />
            <Route path="sales" element={<SalesInterface />} />
            <Route path="products" element={<ProductManagement />} />
            <Route path="categories" element={<CategoryManagement />} />
            <Route path="medias" element={<MediaManagement />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="permissions" element={<Permissions />} />
            <Route
              path="permissions/cancellations"
              element={<CancellationHistory />}
            />
            <Route
              path="permissions/discounts"
              element={<DiscountHistory />}
            />
            <Route path="customers" element={<CustomerManagement />} />
            <Route path="tables" element={<TableManagement />} />
            <Route path="sessions" element={<SessionManagement />} />
            <Route path="keassa" element={<Navigate to="/admin/waise" replace />} />
            <Route path="waise-v1" element={<WaiseLegacyPage />} />
            <Route path="reports" element={<Reports />} />
            <Route path="reports/caisse" element={<CashReports />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* Routes superadmin (sandbox) */}
          <Route path="/superdamin" element={<Navigate to="/superadmin" replace />} />
          <Route path="/superdamin/*" element={<Navigate to="/superadmin" replace />} />
          <Route
            path="/superadmin/*"
            element={
              <ProtectedRoute requiredRole="superadmin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminInterface />} />
            <Route path="dashboard" element={<AdminInterface />} />
            <Route path="products" element={<ProductManagement />} />
            <Route path="sales" element={<SalesInterface />} />
            <Route path="tables" element={<TableManagement />} />
            <Route path="keassa" element={<Navigate to="/superadmin/waise" replace />} />
            <Route path="waise-v1" element={<WaiseLegacyPage />} />
            <Route path="permissions" element={<Permissions />} />
            <Route
              path="permissions/cancellations"
              element={<CancellationHistory />}
            />
            <Route
              path="permissions/discounts"
              element={<DiscountHistory />}
            />
            <Route path="settings" element={<Settings />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="reports" element={<Reports />} />
            <Route path="reports/caisse" element={<CashReports />} />
          </Route>

          {/* Le Super Admin reste dans son espace d'administration. */}
          <Route
            path="/superadmin/caisse"
            element={<Navigate to="/superadmin" replace />}
          />

          {/* KDS superadmin (sandbox) */}
          <Route
            path="/superadmin/cuisine"
            element={
              <ProtectedRoute requiredRole="superadmin">
                <MOOCuisineInterface />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/bar"
            element={
              <ProtectedRoute requiredRole="superadmin">
                <MOOBarInterface />
              </ProtectedRoute>
            }
          />

          {/* Redirection pour routes inconnues */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Statut système */}
        <SystemStatus />

      </div>
    </Router>
  );
}

export default App;
